import type Menu from 'primevue/menu';
import { computed, onMounted, ref, toRaw } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useResource } from '~/composables/useResource';
import { useUsers } from '~/composables/useUsers';
import { useWorkSpace } from '~/composables/useWorkSpace';
import { useAuthStore } from '~/store/auth';
import {Capabilities} from '~/composables/useRolePermissions';
//
import { BaseIcon, SidebarIcon } from '~/types/baseIcon';
// const { signOut } = useAuthComposable()

interface Menu {
    to?: string;
    name: string;
    icon: string;
    external?: boolean;
    activeRouteNames: string[];
    id: keyof typeof Capabilities.SIDEBAR;
}

interface MenuGroup {
    name: string;
    menus: Menu[];
}



enum MenuLabel {
    PROFILE = 'My Profile',
    SWITCH_WORKSPACES = 'Switch Workspaces',
    LOGOUT = 'Logout',
}

const baseMenus: Menu[] = [
    {
        to: 'index',
        name: 'Dashboard',
        icon: SidebarIcon.DASHBOARD,
        activeRouteNames: ['index'],
        id: Capabilities.SIDEBAR.DASHBOARD,
    },
    {
        to: 'businesses',
        name: 'Businesses',
        icon: 'hugeicons:building-01',
        activeRouteNames: [
            'businesses',
            'businesses-businessId-cim',
            'businesses-businessId-proposal',
            'businesses-businessId-appraisal',
            'businesses-businessId-details-activity',
            'businesses-businessId-details-folders',
            'businesses-businessId-details-folders-folderId',
            'businesses-businessId-details-file-sharing',
            'businesses-businessId-details-acquisition-leads',
            'businesses-businessId-details-analytics',
            'businesses-businessId-details-automation-manager',
            'businesses-businessId-details-cim-access-manager',
            'businesses-businessId-details-confidentiality-agreement',
            'businesses-businessId-details-profile',
            'businesses-businessId-details-prospect-manager',
            'businesses-businessId-details-recorder',
            'businesses-businessId-details-sharing',
            'businesses-businessId-details-teaser',
            'businesses-businessId-details-marketing',
        ],
        id: Capabilities.SIDEBAR.BUSINESS,
    },
    {
        to: 'business-stats',
        name: 'Business Stats',
        icon: 'hugeicons:analytics-01',
        activeRouteNames: ['business-stats'],
        id: Capabilities.SIDEBAR.BUSINESS_STATS,
    },
    {
        to: 'pipeline',
        name: 'Pipeline',
        icon: 'bi:kanban',
        activeRouteNames: ['pipeline'],
        id: Capabilities.SIDEBAR.PIPELINE,
    },
    {
        to: 'contracts',
        name: 'Contracts',
        icon: SidebarIcon.CONTRACTS,
        activeRouteNames: [
            'contracts', 
            'contracts-id-details'
        ],
        id: Capabilities.SIDEBAR.CONTRACTS,
    },
    {
        to: 'trust-accounting',
        name: 'Trust Accounting',
        icon: SidebarIcon.TRUST_ACCOUNTING,
        activeRouteNames: [
            'trust-accounting', 
            'trust-accounting-id'
        ],
        id: Capabilities.SIDEBAR.TRUST_ACCOUNTING,
    },
    {
        to: 'contacts',
        name: 'Contacts',
        icon: 'streamline-ultimate-color:multiple-users-2',
        activeRouteNames: [
            'contacts', 
            'contacts-id-activity', 
            'contacts-id-linked-businesses', 
            'contacts-id-advanced-profile', 
            'contacts-id-ownership'
        ],
        id: Capabilities.SIDEBAR.CONTACTS,
    },

    {
        to: 'tasks',
        name: 'Tasks',
        icon: SidebarIcon.TASKS,
        activeRouteNames: ['tasks'],
        id: Capabilities.SIDEBAR.TASKS,
    },
    {
        to: 'templates',
        name: 'Templates',
        icon: SidebarIcon.TEMPLATES,
        activeRouteNames: [
            'templates',
            'templates-appraisal',
            'templates-cim',
            'templates-cim-cimId',
            'templates-confidentiality-agreement',
            'templates-forms',
            'templates-forms-new',
            'templates-forms-id',
            'templates-proposal',
            'templates-proposal-id',
            'templates-industries',
            'templates-resources'
        ],
        id: Capabilities.SIDEBAR.TEMPLATES,
    },
    {
        to: 'automations',
        name: 'Automations',
        icon: SidebarIcon.AUTOMATIONS,
        activeRouteNames: [
            'automations',
            'automations-automationId',
            'automations-automationId-events'
        ],
        id: Capabilities.SIDEBAR.AUTOMATIONS,
    },
    {
        to: 'campaign',
        name: 'Campaigns',
        icon: SidebarIcon.CAMPAIGN,
        activeRouteNames: [
            'campaign',
            'campaign-create',
            'campaign-id'
        ],
        id: Capabilities.SIDEBAR.CAMPAIGNS,
    },
    {
        to: 'calendar',
        name: 'My Calendar',
        icon: SidebarIcon.CALENDAR,
        activeRouteNames: ['calendar'],
        id: Capabilities.SIDEBAR.CALENDAR,
    },
];

const workSpaceMenus: Menu[] = [
    {
        to: 'settings',
        name: 'Settings',
        icon: SidebarIcon.SETTINGS,
        activeRouteNames: ['settings'],
        id: Capabilities.SIDEBAR.SETTINGS,
    },
    {
        to: 'users',
        name: 'Users',
        icon: SidebarIcon.USERS,
        activeRouteNames: ['users'],
        id: Capabilities.SIDEBAR.USERS,
    },
];

let sidebarInstance = null;

export default function useSidebar() {
    if (sidebarInstance) {
        return sidebarInstance;
    }

    const profileMenu = ref<InstanceType<typeof Menu> | null>(null);
    const authStore = useAuthStore();
    const route = useRoute();
    const router = useRouter();

    const loggedInUser = ref(null);
    const workSpace = ref(null);
    const isAdmin = ref(false);
    const resources = ref<Array<{ name: string; link: string }>>([]);

    const menuItems = [
        { label: 'My Profile', icon: 'lucide:user', id: MenuLabel.PROFILE },
        {
            label: 'Switch Workspaces',
            icon: 'lucide:building-2',
            command: () => {
                console.log('switch workspaces');
                const cookie = useCookie('c-wId');
                cookie.value = null;
                console.log('cookie', cookie.value);
            },
            id: MenuLabel.SWITCH_WORKSPACES,
        },
        {
            label: 'Logout',
            icon: 'lucide:log-out',
            command: () => {},
            id: MenuLabel.LOGOUT,
        },
    ];

    const routeName = computed(() => route.name);

    const menus = computed<MenuGroup[]>(() => {
        const { can } = useRolePermissions();
        const isAdmin = useRolePermissions().isAdmin;
        const isOwner = useRolePermissions().isOwner;
        const isBroker = useRolePermissions().isBroker;

        const config = useRuntimeConfig()
        const country = String(config.public.country || 'UNITED_STATES')

        const computedMenus: MenuGroup[] = [
            { name: '', menus: [...baseMenus] },
            { name: 'Workspace', menus: [...workSpaceMenus] },
        ];


        let enabledFeatures = [];
        if(workSpace.value?.enabledFeatures) {
            enabledFeatures = toRaw(workSpace.value?.enabledFeatures);
        }
        
        console.log('enabledFeatures', enabledFeatures);
        
        const hiddenFeatures = new Set(['']);
        const optionalFeatures = new Set(['Pipeline', 'Contracts', 'Tasks', 'Contacts']);
        const australiaOnlyFeatures = new Set(['Trust Accounting'])

        // Filter the base menus
        computedMenus[0].menus = computedMenus[0]?.menus?.filter((menu) => {
            if (!can(menu.id)) return false;
            
            if (hiddenFeatures.has(menu.name)) return false;

            if (optionalFeatures.has(menu.name)) {
                const isEnabled = enabledFeatures.includes(menu.name);
                console.log(`Feature ${menu.name} isEnabled:`, isEnabled);
                return isEnabled;
            }

            if (australiaOnlyFeatures.has(menu.name)) {
                const isEnabled = country === "AUSTRALIA";
                console.log(`Feature ${menu.name} isEnabled:`, isEnabled);
                return isEnabled;
            }

            return true;
        });

        // Filter workspace menus
        computedMenus[1].menus = computedMenus[1]?.menus?.filter(
            (menu) => can(menu.id)
        );

        if (resources.value.length && can(Capabilities.SIDEBAR.RESOURCES)) {
            const tempMenus: MenuGroup = {
                name: 'Resources',
                menus: resources.value.map(resource => ({
                    to: resource.link,
                    name: resource.name,
                    icon: BaseIcon.EXTERNAL_LINK,
                    external: true,
                    activeRouteNames: [],
                    id: Capabilities.SIDEBAR.RESOURCES,
                }))
            };
            computedMenus.push(tempMenus);
        }

        return computedMenus;
    });

    const getInitials = () => {
        return loggedInUser.value?.name
            ? loggedInUser.value.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
            : '';
    };

    const isActive = (menu: Menu) =>
        menu.activeRouteNames.includes(routeName.value as string);

    const navigateTo = (menu: Menu) => {
        if (menu.external && menu.to) {
            window.open(menu.to, '_blank', 'noopener,noreferrer');
        } else if (menu.to) {
            router.push({ name: menu.to });
        }
    };

    const toggleMenu = (event: MouseEvent) => {
        profileMenu.value?.toggle(event);
    };

    const handleMenuItemClick = async (item) => {
        if (item.label === 'My Profile') {
            authStore.setEditProfile(true);
        } else if (item.label === 'Logout' && item.command) {
            // // await signOut()
            // await clerk.signOut()
            // router.push('/auth/sign-in')
        } else if (item.label === 'Switch Workspaces' && item.command) {
            const cookie = useCookie('c-wId');
            cookie.value = null;
            console.log('cookie', cookie.value);
        }
        profileMenu.value?.hide();
    };

    async function initialize() {
        const { getLoggedInUser, getRoles } = useUsers();
        const { getResources } = useResource();
        const { getWorkSpace } = useWorkSpace();


        const userData = await getLoggedInUser();
        if (userData.data) {
            loggedInUser.value = userData.data;
        }

        try {
            const roleData = await getRoles();
            if (roleData.data && loggedInUser.value) {
                const role = roleData.data.find(
                    (role) =>
                        role.id === loggedInUser.value?.UserWorkSpace[0].roleId
                );
                if (role) {
                    loggedInUser.value.role = role.name;
                    if (role.name === 'Admin') {
                        isAdmin.value = true;
                    }
                }
            }
        } catch (e) {
            console.log(e);
        }

        authStore.setUser(loggedInUser.value);

        const res = await getResources();
        if (res.data) {
            resources.value = res.data;
        }


        const workspaceRes = await getWorkSpace();

        if (workspaceRes.data) {
            workSpace.value = workspaceRes.data;

        }
    }

    onMounted(() => {
        initialize();
    });

    sidebarInstance = {
        profileMenu,
        loggedInUser,
        workSpace,
        isAdmin,
        resources,
        menus,
        menuItems,
        MenuLabel,
        getInitials,
        isActive,
        navigateTo,
        toggleMenu,
        handleMenuItemClick,
    };

    return sidebarInstance;
}